import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Subscription } from "rxjs";
import Swal from "sweetalert2";

import { MantenimientoService } from "../mantenimiento.service";
import {
  DetalleMantenimientoComponent,
  DetalleMantenimientoData,
} from "../detalle-mantenimiento/detalle-mantenimiento.component";
import {
  Mantenimiento,
  tipoMantenimiento,
  estatusDe,
} from "../../shared/model/mantenimiento/mantenimiento.model";

/**
 * «Mis solicitudes de mantenimiento»: lo que uno ha pedido del edificio y
 * cómo va.
 *
 * Arriba, lo que espera su visto bueno (resueltas): es lo único que le
 * toca hacer a quien pidió, y si no contesta se cierra sola. Abajo, todas
 * las suyas.
 *
 * Desde el 12/10/2026 solo vive DENTRO de «Nueva solicitud», que pone el
 * encabezado, «Regresar» y «Pedir mantenimiento». Ya no tiene ruta propia:
 * /admin/mantenimiento/solicitud lleva al concentrador.
 */
@Component({
  selector: "app-mis-mantenimiento",
  standalone: true,
  templateUrl: "./mis-mantenimiento.component.html",
  styleUrls: ["../mantenimiento-comun.css", "./mis-mantenimiento.component.css"],
  imports: [CommonModule, RouterLink],
})
export class MisMantenimientoComponent implements OnInit, OnDestroy {
  lista: Mantenimiento[] = [];
  puedeAtender = false;
  cargando = true;

  pagina = 1;
  readonly porPagina = 10;

  private readonly subs = new Subscription();

  constructor(
    private readonly service: MantenimientoService,
    private readonly dialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargar();
    // Del asistente («Ver mi solicitud») o de un correo: ?solicitud=12
    // abre su detalle. Se quita SOLO ese parámetro: dentro de «Nueva
    // solicitud» el resto de la URL dice qué vista se está viendo.
    this.subs.add(
      this.route.queryParamMap.subscribe((q) => {
        const id = Number(q.get("solicitud"));
        if (id > 0) {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { solicitud: null },
            queryParamsHandling: "merge",
            replaceUrl: true,
          });
          this.abrir(id);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  cargar(fondo = false): void {
    if (!fondo) {
      this.cargando = true;
    }
    this.service.mias(fondo).subscribe({
      next: (res) => {
        this.cargando = false;
        if (res?.codigoError) {
          Swal.fire({ icon: "error", text: res.mensajeError || "No se pudieron cargar tus solicitudes." });
          this.cdr.detectChanges();
          return;
        }
        this.lista = res.solicitudes || [];
        this.puedeAtender = !!res.puedeAtender;
        if (this.pagina > this.totalPaginas()) {
          this.pagina = this.totalPaginas();
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        Swal.fire({ icon: "error", text: "No se pudo conectar con el servidor." });
        this.cdr.detectChanges();
      },
    });
  }

  abrir(idSolicitud?: number): void {
    if (!idSolicitud) {
      return;
    }
    const ref = this.dialog.open<DetalleMantenimientoComponent, DetalleMantenimientoData, boolean>(
      DetalleMantenimientoComponent,
      {
        data: { idSolicitud },
        panelClass: "app-standard-dialog",
        backdropClass: "app-standard-dialog-backdrop",
        width: "940px",
        maxWidth: "96vw",
        autoFocus: false,
      },
    );
    ref.afterClosed().subscribe((cambio) => {
      if (cambio) {
        this.cargar(true);
      }
    });
  }

  /* ---------------- Lo que se ve ---------------- */

  get porConfirmar(): Mantenimiento[] {
    return this.lista.filter((s) => s.estatus === "RESUELTA");
  }

  get abiertas(): number {
    return this.lista.filter((s) => ["SOLICITADA", "EN_ATENCION", "EN_ESPERA"].includes(s.estatus || "")).length;
  }

  cat(s: Mantenimiento) {
    return tipoMantenimiento(s.categoria);
  }

  est(s: Mantenimiento) {
    return estatusDe(s.estatus);
  }

  /** «2026-09-29 10:15» → «29 sep 2026». */
  dia(f?: string | null): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(f || "");
    if (!m) {
      return f || "";
    }
    return new Date(+m[1], +m[2] - 1, +m[3])
      .toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })
      .replace(".", "");
  }

  /** Quién la tiene, dicho para quien la pidió. */
  quien(s: Mantenimiento): string {
    switch (s.estatus) {
      case "SOLICITADA": return "Aún no la toma nadie";
      case "CANCELADA": return s.idUsuarioCancela === s.idUsuarioSolicita ? "La retiraste" : "No procedió";
      default: return s.responsable || "—";
    }
  }

  trackSol(_: number, s: Mantenimiento): number | undefined {
    return s.idSolicitud;
  }

  /* ---------------- Paginado (en el navegador) ---------------- */

  totalPaginas(): number {
    return Math.max(1, Math.ceil(this.lista.length / this.porPagina));
  }

  paginaActual(): Mantenimiento[] {
    const desde = (this.pagina - 1) * this.porPagina;
    return this.lista.slice(desde, desde + this.porPagina);
  }

  irPagina(p: number): void {
    this.pagina = Math.min(Math.max(1, p), this.totalPaginas());
  }

  paginas(): number[] {
    return Array.from({ length: this.totalPaginas() }, (_, i) => i + 1);
  }

  rangoInicio(): number {
    return this.lista.length ? (this.pagina - 1) * this.porPagina + 1 : 0;
  }

  rangoFin(): number {
    return Math.min(this.pagina * this.porPagina, this.lista.length);
  }
}
